import { assertNotEmpty } from '@festina-life/core'
import { useTranslate } from '@festina-life/ui/hooks'
import { ConfirmationBox } from '@festina-life/ui/structure'
import { useCallback, useMemo, useState } from 'react'

import { useTemplateContext } from '../../context/templateContext'
import { useLazyLayerMetaModel } from '../../context/useLazyLayerMetaModel'
import {
  type DynamicConfigurationTemplateFunctionValue,
  type FPlusExpressionValue,
  isTemplateVersion,
} from '../../types/templateModel'
import { functionPath } from '../../uiModels/transformers/toVisualModelConverter'
import { TemplateFieldExpression } from '../templateFieldExpression/TemplateFieldExpression'
import { functionTitle } from '../templateFieldsTable/fieldsHelpers'
import { functionIdentifier } from '../templateFieldsTable/functionHelpers'
import {
  addDynamicFunctionToSubTemplatedField,
  addOrReplaceDynamicFunctionOnTemplate,
} from './helpers'
import type {
  SubTemplateAddDynamicFunctionFields,
  TemplateVersionAddDynamicFunctionFields,
} from './types'

export const AddDynamicFunctionValueCell = (
  props:
    | SubTemplateAddDynamicFunctionFields
    | TemplateVersionAddDynamicFunctionFields,
) => {
  const { templateVersion } = useTemplateContext()
  const metaModel = useLazyLayerMetaModel(templateVersion?.layer?.layerId)
  const [duplicatedFunctionValue, setDuplicatedFunctionValue] =
    useState<DynamicConfigurationTemplateFunctionValue>()
  const [placeholderKey, setPlaceholderKey] = useState(self.crypto.randomUUID())
  const translate = useTranslate()

  const path = useMemo(() => {
    if (isTemplateVersion(props.parent)) {
      return props.parent.path
    }

    const typedProps = props as SubTemplateAddDynamicFunctionFields

    return `${typedProps.parent.path}{${typedProps.subTemplateIdentifier}}`
  }, [props])

  const handleAddFunctionValue = useCallback(
    async (newFunction: DynamicConfigurationTemplateFunctionValue) => {
      if (isTemplateVersion(props.parent)) {
        await props.parent.updateTemplate(
          addOrReplaceDynamicFunctionOnTemplate(props.parent, newFunction),
        )
      } else {
        const typedProps = props as SubTemplateAddDynamicFunctionFields
        const subTemplatedField = props.parent

        addDynamicFunctionToSubTemplatedField({
          dynamicFunction: newFunction,
          subTemplatedField,
          subTemplateIdentifier: typedProps.subTemplateIdentifier,
          metaModel,
        })
      }

      // The internal state of the placeholder has to be reset. We do this by forcing a new key on the component.
      setPlaceholderKey(self.crypto.randomUUID())
    },
    [metaModel, props],
  )

  const duplicatedFunctionTitle = useMemo(() => {
    if (duplicatedFunctionValue) {
      return functionTitle(duplicatedFunctionValue)
    }
    return ''
  }, [duplicatedFunctionValue])

  const handleCodeChanged = useCallback(
    async (nextValue: FPlusExpressionValue) => {
      const dynamicFunctionPath = functionPath({
        basePath: path,
        source: nextValue.fplusSource ?? '',
      })

      const createdDynamicFunction = {
        function: nextValue,
        path: dynamicFunctionPath,
        functionIdentifier: functionIdentifier({
          source: nextValue.fplusSource,
        }),
        type: 'dynamic',
        origin: dynamicFunctionPath,
        // The update functions are regenerated for the dynamic function when the root operation is updating and the
        // visual model is regenerated, so this can just be a no-op
        update: () => {},
        updateRaw: () => {},
        isParentNotRelevant: false,
      } satisfies DynamicConfigurationTemplateFunctionValue

      let isDuplicatedFunction = false

      if (isTemplateVersion(props.parent)) {
        isDuplicatedFunction = [
          ...(props.parent.dynamicFunctions ?? []),
          ...(props.parent.declaredFunctions ?? []),
        ].some(
          (templateFunction) =>
            templateFunction.functionIdentifier ===
            createdDynamicFunction.functionIdentifier,
        )
      } else {
        const typedProps = props as SubTemplateAddDynamicFunctionFields
        const subTemplatedField = props.parent
        const variant = subTemplatedField.value?.variants.find(
          (variant) =>
            variant.subTemplateIdentifier === typedProps.subTemplateIdentifier,
        )
        isDuplicatedFunction = [
          ...(variant?.dynamicFunctions ?? []),
          ...(variant?.declaredFunctions ?? []),
        ].some(
          (templateFunction) =>
            templateFunction.functionIdentifier ===
            createdDynamicFunction.functionIdentifier,
        )
      }

      if (isDuplicatedFunction) {
        setDuplicatedFunctionValue(createdDynamicFunction)
        throw new Error('Duplicated function value')
      } else {
        await handleAddFunctionValue(createdDynamicFunction)
      }
    },
    [handleAddFunctionValue, path, props],
  )

  const emptyFunctionPlaceholder: FPlusExpressionValue = useMemo(
    () => ({ fplusSource: undefined }),
    [],
  )

  return (
    <>
      <ConfirmationBox
        isOpen={!!duplicatedFunctionValue}
        onAccept={async () => {
          assertNotEmpty(duplicatedFunctionValue)

          await handleAddFunctionValue(duplicatedFunctionValue)
          setDuplicatedFunctionValue(undefined)
        }}
        onCancel={() => setDuplicatedFunctionValue(undefined)}
        title={translate(
          'templateViewer.addDynamicFunction.confirmation.duplicate.title',
          {
            functionNotation: duplicatedFunctionValue
              ? duplicatedFunctionTitle
              : '',
          },
        )}
        cancelLabel={translate('no')}
        confirmLabel={translate('yes')}
      >
        <div className="flex flex-col gap-y-2">
          {translate(
            'templateViewer.addDynamicFunction.confirmation.duplicate.description',
          )}
        </div>
      </ConfirmationBox>
      <TemplateFieldExpression
        key={placeholderKey}
        expression={emptyFunctionPlaceholder}
        isPlaceholder
        isExpanded={false}
        onChange={handleCodeChanged}
        path={path}
        fallbackCode=""
        fromOrigin={path}
      />
    </>
  )
}
